import type { LucideIcon } from 'lucide-react';
import type { NavGroup, NavItem } from './nav';
import type { RecentPlace } from './recentPlaces';

/** Ein direkt anspringbares Ziel der Navigation — Haupt- ODER Unterpunkt, flach. */
export interface NavLeaf {
  label: string;
  /** Kurzlabel für enge Kacheln (`NavItem.short`); bei Unterpunkten = `label`. */
  short: string;
  to: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Label des Hauptpunkts, wenn dies ein Unterpunkt ist („CRM" bei „Ansprechpartner"). */
  parentLabel?: string;
}

/** Haupt- und Unterpunkte als flache Liste (für Suche, Verlauf und Ortsangabe). */
export function navLeaves(items: NavItem[]): NavLeaf[] {
  const out: NavLeaf[] = [];
  for (const item of items) {
    out.push({ label: item.label, short: item.short, to: item.to, icon: item.icon, group: item.group });
    for (const child of item.children ?? []) {
      if (child.to === item.to) continue;
      out.push({ label: child.label, short: child.label, to: child.to, icon: item.icon, group: item.group, parentLabel: item.label });
    }
  }
  return out;
}

function pathOf(to: string): string {
  return to.split('?')[0];
}

/** Module, deren Name (oder Hauptpunkt) den Suchbegriff enthält. */
export function matchLeaves(leaves: NavLeaf[], query: string): NavLeaf[] {
  const needle = query.trim().toLocaleLowerCase('de');
  if (!needle) return [];
  return leaves.filter((leaf) => `${leaf.label} ${leaf.parentLabel ?? ''}`.toLocaleLowerCase('de').includes(needle));
}

/** Das Navigationsziel genau dieser Adresse (für „Zuletzt geöffnet"), sonst `undefined`. */
export function exactLeaf(leaves: NavLeaf[], pathname: string, search: string): NavLeaf | undefined {
  return leaves.find((leaf) => leaf.to === pathname + search) ?? leaves.find((leaf) => leaf.to === pathname);
}

/**
 * Wo bin ich? — Unterzeile im „Wohin?"-Knopf. Ein gemerkter Datensatz dieser
 * Adresse ist am genauesten („51853 · ARA Attnang"), sonst das Modul mit dem
 * längsten passenden Pfad (`/verkauf/123` → „Verkauf").
 */
export function currentPlaceLabel(pathname: string, leaves: NavLeaf[], recents: RecentPlace[]): string {
  const recent = recents.find((place) => place.path === pathname);
  if (recent) return recent.label;
  let best: NavLeaf | undefined;
  for (const leaf of leaves) {
    const path = pathOf(leaf.to);
    const isMatch = path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
    if (isMatch && (!best || path.length > pathOf(best.to).length)) best = leaf;
  }
  return best?.label ?? '';
}

/**
 * Angeheftete Ziele: die vier am Handy meistgebrauchten Module, soweit sichtbar;
 * fehlt eins (Recht, ausgeblendete Gruppe), rückt das nächste der Navigation nach.
 */
export const PINNED_PATHS = ['/', '/projekte', '/zeiterfassung', '/verkauf'];

export function pinnedLeaves(items: NavItem[]): NavLeaf[] {
  const leaves = navLeaves(items).filter((leaf) => !leaf.parentLabel);
  const preferred = PINNED_PATHS.map((path) => leaves.find((leaf) => leaf.to === path)).filter(
    (leaf): leaf is NavLeaf => Boolean(leaf),
  );
  const rest = leaves.filter((leaf) => !preferred.includes(leaf));
  return [...preferred, ...rest].slice(0, 4);
}
